import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SHOWCASE_PROJECT_QUERY_KEY } from './useGetShowcaseProject';
import { SHOWCASE_PROJECT_MEDIA_QUERY_KEY } from './useGetShowcaseProjectMedia';

import type { ShowcaseProject, CreateShowcaseProjectParams } from '../types';

import {
  request,
  type V3ApiSuccessfulResponse,
  type V3ApiFailedResponse,
} from 'javascripts/services/v3ApiClient';
import type { ProjectDatabaseId } from 'data/v1';
import Steven from 'helpers/steven';

type UpdateShowcaseProjectParams = Partial<CreateShowcaseProjectParams> & {
  visible?: boolean;
  visibleMedia?: number[];
  hiddenMedia?: number[];
};
type UpdateShowcaseProjectResponse = V3ApiSuccessfulResponse<ShowcaseProject>;

export const useUpdateShowcaseProject = ({
  projectDatabaseId,
  onSuccess,
  onError,
}: {
  projectDatabaseId: ProjectDatabaseId;
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateShowcaseProjectResponse,
    V3ApiFailedResponse,
    UpdateShowcaseProjectParams
  >({
    mutationFn: async (params) => {
      return request({
        url: `projects/${projectDatabaseId}/showcase_project`,
        method: 'put',
        data: params,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SHOWCASE_PROJECT_QUERY_KEY, projectDatabaseId],
      });
      queryClient.invalidateQueries({
        queryKey: [SHOWCASE_PROJECT_MEDIA_QUERY_KEY, projectDatabaseId],
        refetchType: 'all',
      });
      Steven.entityCrud({
        entity_id: `${projectDatabaseId}`,
        entity_type: 'showcase_project',
        action: 'updated',
      });

      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};
